import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useFeatures, isFinancingAvailable, type FeatureFlags } from "@/lib/features";
import { useToast } from "@/hooks/use-toast";
import type { FeatureState } from "@/lib/use-profile-config";

interface FeatureGateProps {
  feature: keyof FeatureFlags;
  children: ReactNode;
  fallback?: ReactNode;
  profileState?: FeatureState;
  upgradeMessage?: string;
  tierRequired?: string;
}

const FEATURE_MIN_TIER: Record<string, string> = {
  financing: "Starter",
  automations: "Starter",
  contractToCash: "Growth",
  aiReports: "Starter",
  multiEntity: "Growth",
  communications: "Starter",
  arInstruments: "Growth",
  reconciliation: "Custom",
  contractInvoicing: "Growth",
  apMatching: "Custom",
  externalApi: "Growth",
  directDebit: "Starter",
  customPaymentGateways: "Growth",
  chartOfAccounts: "Starter",
  purchaseOrders: "Custom",
  deals: "Growth",
};

export const FEATURE_LABELS: Record<string, string> = {
  ap: "Accounts Payable",
  ar: "Accounts Receivable",
  financing: "Financing",
  automations: "Automations",
  contractToCash: "Contract to Cash",
  aiReports: "AI Reports",
  aiChat: "AI Chat",
  multiEntity: "Multi-Entity",
  tasks: "Tasks",
  communications: "Communications",
  activityLog: "Activity Log",
  arInstruments: "AR Instruments",
  reconciliation: "Reconciliation",
  contractInvoicing: "Contract Invoicing",
  apMatching: "AP Matching",
  customPaymentGateways: "Custom Payment Gateways",
  paymentGateways: "Custom Payment Gateways",
  externalApi: "External API",
  directDebit: "Direct Debit",
  chartOfAccounts: "Chart of Accounts",
  purchaseOrders: "Purchase Orders",
  deals: "Deals",
};

function RedirectWithToast({ feature }: { feature: string }) {
  const { t } = useTranslation("common");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const didRedirect = useRef(false);
  const minTier = FEATURE_MIN_TIER[feature] || "Growth";
  const label = FEATURE_LABELS[feature] || feature;

  useEffect(() => {
    if (didRedirect.current) return;
    didRedirect.current = true;
    toast({
      title: t("featureGate.notAvailable", { feature: label }),
      description: t("featureGate.locked", { tier: minTier }),
    });
    setLocation("/", { replace: true });
  }, []);

  return null;
}

export function FeatureGate({ feature, children, fallback, profileState }: FeatureGateProps) {
  const features = useFeatures();

  if (feature === "tier") return <>{children}</>;

  const enabled = features[feature];

  if (profileState === "HIDDEN" && !enabled) return null;

  if (enabled === false) {
    return <>{fallback !== undefined ? fallback : <RedirectWithToast feature={feature} />}</>;
  }

  if (feature === "financing" && !isFinancingAvailable(features)) {
    return <>{fallback !== undefined ? fallback : <RedirectWithToast feature={feature} />}</>;
  }

  return <>{children}</>;
}
